import React, { FunctionComponent, useState } from 'react'
import { InjectedIntlProps, injectIntl } from 'react-intl'
import { Box } from 'src/components/calendar/Box'
import { EventSection } from 'src/components/calendar/eventSection'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { trackGetTickets } from 'src/lib/analytics'
import { guessZone } from 'src/lib/display-date'
import { formattedMultipleDateRange, formattedTimeOnlyRange, formattedTime } from 'src/lib/formatter'
import { DATE_AND_TIME_ID, MAX_DATE_BOXES_TO_SHOW, DATE_DISPLAY_OPTIONS } from './constants'
import locale from './locale'

const style = require('./DateAndTime.css')

interface Props {
  eventData: any,
  eventTimeSlotData: any,
  loading: Boolean,
  viewer: any,
}

declare var $u

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingContent width="170px" height="25px" borderRadius="rounder" />
    <LoadingContent width="138px" height="62px" borderRadius="sharper" />
  </LoadingPlaceholder>
)

const DateAndTimeComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  eventTimeSlotData,
  loading,
  intl,
  viewer,
}) => {
  const { formatMessage } = intl
  const [selectedSlot, setSelectedSlot] = useState(-1)
  const [showAll, setShowAll] = useState(false)

  const renderBoxes = slots => slots.map((event, idx) => (renderBox(event, idx)))

  const renderBox = (event, idx) => {
    let { startAt, endAt } = event
    let tz = null

    if (eventData.virtual) {
      startAt = new Date(1000 * event.startStamp)
      endAt = new Date(1000 * event.endStamp)
      tz = guessZone(startAt)
    }

    const formattedTimeOnly = () => {
      const { hiddenDate, dateDisplayOption } = eventData

      if (hiddenDate || dateDisplayOption === DATE_DISPLAY_OPTIONS.hideEventTime) return null
      if (dateDisplayOption === DATE_DISPLAY_OPTIONS.startTimeOnly) return formattedTime(startAt, intl, tz)

      return formattedTimeOnlyRange({ startAt, endAt, intl, tz })
    }

    const formattedDate = () => {
      const { dateDisplayOption } = eventData
      if (dateDisplayOption === DATE_DISPLAY_OPTIONS.startTimeOnly) {
        return formattedMultipleDateRange({ startAt, endAt: startAt, intl, totalCount: 1, showYear: true })
      }
      return formattedMultipleDateRange({ startAt, endAt, intl, totalCount: 1, showYear: true })
    }

    return (
      <Box
        key={idx}
        date={formattedDate()}
        time={formattedTimeOnly()}
        isOnSale={isOnSale(event, eventTimeSlotData.allAvailableTimeSlots)}
        handleClick={() => selectDate(idx, eventData)}
        selected={isSlotSelected(idx)}
      />
    )
  }

  const isOnSale = (timeSlot, availableSlots) => (
    timeSlot.state !== 'FULL' &&
    availableSlots.some(slot => ((slot.startAt === timeSlot.startAt) && (slot.endAt === timeSlot.endAt)))
  )

  const selectDate = (idx, eventData) => {
    setSelectedSlot(idx)
    getJunoUrl(eventData, idx)
  }

  const isSlotSelected = idx => (idx === selectedSlot)

  const getJunoUrl = (eventData, idx) => {
    const eventId = eventData.id
    const hostId = eventData.user.id
    const timedEntry = eventData.timedEntry
    const timeslot = eventTimeSlotData.allTimeSlots[idx]
    const timeslotDuration = timeslot.endStamp - timeslot.startStamp
    const selectedDate = new Date(`${timeslot.startAt.split('T')[0]}T00:00:00`)
    const timeslotStartUtc = new Date(1000 * timeslot.startStamp).toISOString()
    $u.open(eventId, {
      currentEventId: timeslot.id,
      selectedDate,
      timeslotStart: timeslotStartUtc,
      timeslotDuration,
    })
    const trackingParams = { timedEntry, eventId, hostId, widget: 'date_time_button', viewer }
    trackGetTickets(trackingParams)
  }

  const determineSlicedTimeSlots = () => {
    if (eventData?.totalTimeSlotCount === 6 || eventData?.totalTimeSlotCount === 8) {
      return eventTimeSlotData.allTimeSlots.slice(0, MAX_DATE_BOXES_TO_SHOW)
    }

    return eventTimeSlotData.allTimeSlots.slice(0, MAX_DATE_BOXES_TO_SHOW - 1)
  }

  const getTimeSlots = () => (
    showAll
      ? eventTimeSlotData.allTimeSlots
      : determineSlicedTimeSlots()
    /* TODO: This is a quick fix for rendering exactly six or eight timeslots depending on screen width,
       we'll want to come back to this at some point and find a tidier solution for this issue. */
  )

  const showMoreLessBox = (eventData && eventData.totalTimeSlotCount > MAX_DATE_BOXES_TO_SHOW)

  if (loading) return <Placeholder />

  if (eventData.hiddenDate || eventData.dateDisplayOption === DATE_DISPLAY_OPTIONS.hideEventTime) return null

  return (
    <EventSection id={style.dateTime} sectionId={DATE_AND_TIME_ID} heading={formatMessage(locale.dateAndTime)}>
      <div className={style.container}>
        {eventData && renderBoxes(getTimeSlots())}
        {showMoreLessBox &&
          <div className={style.showMoreLessItems} onClick={() => setShowAll(!showAll)}>
            <div className={style.showMoreLessItemsText}>
              {showAll ? formatMessage(locale.lessDatesAndTimes) : formatMessage(locale.moreDatesAndTimes)}
            </div>
          </div>
        }
      </div>
    </EventSection>
  )
}

export const DateAndTime = injectIntl(DateAndTimeComponent)
